import {inject, observer} from "mobx-react";
import React, {useEffect, useMemo, useState, useCallback, useRef} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPencilAlt, faSpinner, faWindowClose, faCog, faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";
import Checkbox from "../components/checkbox";
import Modal from "../components/modal";
import ExpenseNavigationBar from "../components/expense_configuration-navigation-bar";
import CCardsConfigurationNavigationBar from "../components/c-card-configuration-navigation-bar";
import {fetch} from "../utils/api-wrapper";
import FilterInputField from "../components/filter-input-field";
import PaginationControls from "../components/pagination-controls";
import Utils from "../utils/utils";
import { getSelectStyles } from "../utils/utils";
import Select from 'react-select';
import { imputationDocumentId } from "./review-expense/utils/contract-expense-json";
import { downloadImputationImportTemplateCsv } from "../utils/imputation-import-csv-template";
import {
    imputationConfigRowDisplayName,
    imputationRowHasParentId,
    normalizeDepartmentIdsFromApi,
    normalizeRankIdsFromApi,
    resolveImputationEditTarget,
    IMPUTATION_FETCH_PAGE_SIZE,
} from "../utils/expenses-imputations-config-helpers";
import { useImputationDirectory } from "../utils/use-imputation-directory";
import ImputationConfigModal from "../components/imputation-config-modal";
import {loadXpensCategories, getXpensCategoryLabel} from "../utils/xpens-categories";
import {isImputationLinkStale} from "../utils/imputation-link-stale";
import HoverInfoCard from "../components/hover-card";
import ImportResultModal from "../components/import-result-modal";

const IMPUTATION_TABLE_SORTING = [{ id: "category", desc: false }];

const IMPUTATION_IMPORT_SUMMARY_SPEC = [
    { key: "rows_imported", labelKey: "imputation_import_rows_imported" },
    { key: "rows_failed", labelKey: "imputation_import_rows_failed" },
    { key: "parents_created", labelKey: "imputation_import_parents_created" },
    { key: "parents_reused", labelKey: "imputation_import_parents_reused" },
    { key: "children_created", labelKey: "imputation_import_children_created" },
    { key: "children_reused", labelKey: "imputation_import_children_reused" },
];

/**
 * Normalises a POST /imputations/import response into the shared
 * {status, summary, errors, alerts} shape ImportResultModal expects. Production
 * payloads nest counts under `summary`; root keys kept as a fallback for
 * legacy mocks. rows_imported is derived, never read directly — the API
 * never sends it, only rows_total (which is itself never displayed).
 */
const normalizeImputationImportResult = (result) => {
    const r = result ?? {};
    const s = r.summary && typeof r.summary === "object" ? r.summary : {};
    const rowsFailedRaw = s.rows_failed ?? r.rows_failed ?? r.failed ?? 0;
    const rowsFailed = Number(rowsFailedRaw) || 0;
    const rowsTotal = s.rows_total ?? r.rows_total;
    const totalNum = Number(rowsTotal);
    const rowsImported = Number.isFinite(totalNum)
        ? Math.max(0, totalNum - rowsFailed)
        : Number(r.rows_imported ?? r.success ?? 0) || 0;
    return {
        // Carried through untouched so ImportResultModal can state the outcome
        // (XPEN-3320). This function rebuilds the response rather than spreading
        // it, so anything not listed here is dropped — status included, until
        // this line. Left undefined when the response has none, which the modal
        // treats as "no outcome line" rather than guessing.
        status: r.status,
        summary: {
            rows_imported: rowsImported,
            rows_failed: rowsFailed,
            parents_created: Number(s.parents_created ?? r.parents_created ?? 0) || 0,
            parents_reused: Number(s.parents_reused ?? r.parents_reused ?? 0) || 0,
            children_created: Number(s.children_created ?? r.children_created ?? 0) || 0,
            children_reused: Number(s.children_reused ?? r.children_reused ?? 0) || 0,
        },
        errors: r.errors ?? [],
        alerts: Array.isArray(r.alerts) ? r.alerts : [],
    };
};

const IMPUTATIONS_VISIBLE_COLUMNS_STORAGE_KEY = "xpens.imputations.visible-columns.v1";
const DEFAULT_VISIBLE_COLUMNS = {
    origin: true,
    departments: true,
    ranks: true,
    glNatural: true,
    costType: true,
    tax: true,
    approvers: true,
    defaultExpenseType: false,
    clientFilter: false,
};

const ExpensesImputations = ({data, isCreditCardImputations = false}) => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [imputations, setImputations] = useState([]);
    const [showImputationModal, setShowImputationModal] = useState(false);
    const [imputationModalInitial, setImputationModalInitial] = useState(null);
    const [imputationsDeleteSelection, setImputationsDeleteSelection] = useState([]);
    const [showDeleteImputationModal, setShowDeleteImputationModal] = useState(false);
    const [showAddDefaultImputationsModal, setShowAddDefaultImputationsModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [imputationImportResult, setImputationImportResult] = useState(null);

    const [pageSize, setPageSize] = useState(10);
    const [filterValue, setFilterValue] = useState("");
    const [filterSubcategory, setFilterSubcategory] = useState("all");
    const [filterOrigin, setFilterOrigin] = useState("");
    const [page, setPage] = useState(1);

    // Scoped to expense catalog — this page manages expense imputations only.
    const { parentIds, loadImputationDirectory } = useImputationDirectory("expense");

    const [approvableUsers, setApprovableUsers] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [rankOptions, setRankOptions] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const settingsPanelRef = useRef(null);

    const categoryLabel = (category) => getXpensCategoryLabel(data, category);

    const rowDisplayName = (imputation) => imputationConfigRowDisplayName(imputation, categoryLabel, data);

    // XPEN-3192 / D4: warn, don't silently break, when a category's link
    // points at a retired standard category. data.xpensCategories is already
    // loaded for this page (see the loadXpensCategories effect below).
    const linkStaleWarning = (imputation) => {
        if (!isImputationLinkStale(imputation?.maps_from_verify_category, data.xpensCategories)) return null;
        return (
            <HoverInfoCard
                info={<FontAwesomeIcon icon={faExclamationTriangle} color="orange" className="ml-2" />}
                additionalInfo={
                    <div className="alert alert-danger m-0" style={{ minWidth: "220px" }}>
                        {data.t.read("imputation_link_stale_warning")}
                    </div>
                }
                custom={true}
                position="pos-top-right"
            />
        );
    };

    const originLabel = (origin) => {
        if (origin === "verify_default") return data.t.read("imputation_origin_verify_default");
        if (origin === "client") return data.t.read("imputation_origin_client");
        return origin ?? "";
    };

    const readLabel = useCallback((key, fallback) => {
        const value = data.t.read(key);
        return (typeof value === "string" && value.trim() !== "") ? value : fallback;
    }, [data.t]);

    const hasAderantIntegration = Utils.hasAderantIntegration(data);
    const showRanksColumn = hasAderantIntegration && visibleColumns.ranks;
    // XPEN-3493: one predicate for the whole page — see the same constant in
    // expenses_rates.js and imputation-config-modal.js. This page renders on two
    // routes (Expenses and Credit Card) and both share this one value, which is
    // deliberate: the same screen must not grant different rights by door.
    const canEdit = Utils.userHasRole(data, ["SuperAdmin", "Admin", "Manager"]);

    const filterOriginSelectOptions = useMemo(() => [
        { value: "", label: data.t.read("imputations_filter_origin_all") },
        { value: "client", label: data.t.read("imputation_origin_client") },
        { value: "verify_default", label: data.t.read("imputation_origin_verify_default") },
    ], [data.t]);

    const filterSubcategorySelectOptions = useMemo(() => [
        { value: "all", label: readLabel("imputations_filter_subcategory_all", "All categories") },
        { value: "with", label: readLabel("imputations_filter_subcategory_with", "With subcategories") },
        { value: "without", label: readLabel("imputations_filter_subcategory_without", "Without subcategories") },
    ], [readLabel]);

    const refreshImputations = useCallback(() => {
        // Fixed catalog scope — imputations page shows expense catalog only.
        const filters = [{ id: "imputation_catalog", value: "expense" }];
        if (filterValue && filterValue.trim() !== "") {
            filters.push({id: "search", value: filterValue.trim()});
        }
        if (filterOrigin) {
            filters.push({ id: "origin", value: filterOrigin });
        }
        const allImputations = [];
        const fetchPage = (start) => {
            const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(IMPUTATION_TABLE_SORTING))}&start=${start}&page_size=${IMPUTATION_FETCH_PAGE_SIZE}`;
            fetch(`/imputations/${searchData}`, "GET", {},
                (response) => {
                    const rows = response?.data?.imputations ?? [];
                    const totalRowCount = response?.data?.meta?.totalRowCount ?? 0;
                    allImputations.push(...rows);
                    if (rows.length > 0 && allImputations.length < totalRowCount) {
                        fetchPage(start + rows.length);
                    } else {
                        setImputations([...allImputations]);
                        setLoading(false);
                    }
                });
        };
        fetchPage(0);
    }, [filterValue, filterOrigin]);

    useEffect(() => {
        refreshImputations();
    }, [refreshImputations]);

    useEffect(() => {
        loadXpensCategories(data);
    }, [data]);

    useEffect(() => {
        setPage(1);
    }, [pageSize, filterValue, filterOrigin, filterSubcategory]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(IMPUTATIONS_VISIBLE_COLUMNS_STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            setVisibleColumns((prev) => ({
                ...prev,
                ...(parsed && typeof parsed === "object" ? parsed : {}),
            }));
        } catch (_) {
            // Ignore malformed local storage and keep defaults.
        }
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(IMPUTATIONS_VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns));
        } catch (_) {
            // Ignore persistence errors (private mode, quota, etc).
        }
    }, [visibleColumns]);

    useEffect(() => {
        if (!isSettingsOpen) return undefined;

        const onMouseDown = (event) => {
            if (!settingsPanelRef.current) return;
            if (!settingsPanelRef.current.contains(event.target)) {
                setIsSettingsOpen(false);
            }
        };

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsSettingsOpen(false);
            }
        };

        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [isSettingsOpen]);

    useEffect(() => {
        fetch("/client/users/approvers", "GET", {}, (response) => {
            const users = response?.data ?? [];
            setApprovableUsers(users.map(u => ({
                value: u.id_user,
                label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.id_user,
            })));
        });
    }, []);

    useEffect(() => {
        const searchData = `?filters=${encodeURIComponent(JSON.stringify([{ id: "id_client", value: data.id_client }]))}&sorting=${encodeURIComponent(JSON.stringify([]))}&start=0&page_size=100`;
        fetch(`/departments/${searchData}`, "GET", {}, (response) => {
            const depts = response?.data?.departments ?? [];
            setDepartmentOptions(
                depts
                    .map((d) => {
                        const id = d._id?.$oid ?? d._id ?? d.id;
                        if (id == null || id === "") return null;
                        return { value: String(id), label: d.name || "" };
                    })
                    .filter(Boolean)
            );
        });
    }, [data.id_client]);

    useEffect(() => {
        if (!hasAderantIntegration) {
            setRankOptions([]);
            return;
        }
        fetch("/client/options/?field_name=Rank", "GET", {}, (response) => {
            setRankOptions(Utils.mapRankOptionsFromApi(response?.data));
        });
    }, [hasAderantIntegration]);

    // XPEN-3688: say what the server really did. `added` holds the inserted
    // categories (name_en); `legacy_categories` means nothing was added because
    // the client still holds old-list defaults (shown as stored). An older API
    // without `added` keeps the generic message. Read defensively and keep only
    // text items: a throw here would reach the error callback and report a
    // failure after a success.
    const defaultImputationsResultMessage = (body) => {
        const textItems = (list) => list.filter((item) => typeof item === "string");
        const legacy = Array.isArray(body?.legacy_categories) ? textItems(body.legacy_categories) : [];
        if (legacy.length > 0) {
            return data.t.read("default_imputations_legacy").replace("{names}", () => legacy.join(", "));
        }
        if (!Array.isArray(body?.added)) {
            return data.t.read("default_imputations_success");
        }
        const added = textItems(body.added);
        if (added.length === 0) {
            return data.t.read("default_imputations_nothing_to_add");
        }
        const names = added.map((name) => getXpensCategoryLabel(data, name)).join(", ");
        return data.t.read("default_imputations_added")
            .replace("{n}", String(added.length))
            .replace("{names}", () => names);
    };

    const addDefaultImputations = () => {
        // API hardcodes imputation_catalog:"expense" on every default row (imputations_service.py, add_default_imputations).
        fetch("/imputations/add-defaults", "POST", {},
            (response) => {
                setShowAddDefaultImputationsModal(false);
                setSuccessMessage(defaultImputationsResultMessage(response?.data));
                loadImputationDirectory();
                refreshImputations();
            },
            () => {
                setShowAddDefaultImputationsModal(false);
                setErrorMessage(data.t.read("default_imputations_failure"))
                setLoading(false);
            }
        );
    };

    const prepareImputationImport = (files) => {
        if (files.length > 0) {
            setImportFile(files[0]);
        }
    };

    const processImputationImport = () => {
        if (importFile == null) {
            setErrorMessage(readLabel("imputation_import_no_file", "Please select a CSV or Excel file."));
            return;
        }

        const lowerName = (importFile.name || "").toLowerCase();
        const formData = new FormData();
        if (lowerName.endsWith(".xlsx")) {
            formData.append("excel", importFile);
        } else if (lowerName.endsWith(".csv")) {
            formData.append("csv", importFile);
        } else {
            setErrorMessage(readLabel("imputation_import_invalid_file", "Please upload a CSV or .xlsx file."));
            return;
        }

        fetch("/imputations/import", "POST", formData,
            (response) => {
                const result = response?.data ?? {};
                setShowImportModal(false);
                setImportFile(null);
                setImputationImportResult(normalizeImputationImportResult(result));
                loadImputationDirectory();
                refreshImputations();
            },
            (e) => {
                setShowImportModal(false);
                const apiError = e?.response?.data?.error;
                if (apiError === 'import_validation_failed') {
                    const result = e?.response?.data ?? {};
                    setImputationImportResult(normalizeImputationImportResult(result));
                } else {
                    setErrorMessage(readLabel("imputation_import_failure", "Failed to import imputations."));
                }
            },
            true
        );
    };

    const handleImputationRowClick = useCallback((imputation) => {
        const { parentRow, focusedChildId, isParentMode } = resolveImputationEditTarget(
            imputations,
            imputation,
            parentIds
        );
        setImputationModalInitial({ parentRow, focusedChildId, isParentMode });
        setShowImputationModal(true);
    }, [imputations, parentIds]);

    const deleteImputations = () => {
        fetch("/imputations/", "DELETE", { "imputation_ids": imputationsDeleteSelection },
            () => {
                loadImputationDirectory();
                refreshImputations();
                setImputationsDeleteSelection([])
                setShowDeleteImputationModal(false)
            },
            () => {
                setErrorMessage(data.t.read("failed_to_delete"))
                setLoading(false);
                setShowDeleteImputationModal(false)
            }
        );
    }

    const cancelDeleteImputationModal = () => {
        setShowDeleteImputationModal(false);
        setImputationsDeleteSelection([])
    }

    const isAllImputationsSelected = () => {
        if (!displayedParentRows.length) {
            return false;
        }
        return displayedParentRows.every(imputation => imputationsDeleteSelection.includes(imputationDocumentId(imputation)));
    }

    const isImputationSelected = (imputation) => {
        return imputationsDeleteSelection.includes(imputationDocumentId(imputation));
    }

    const toggleAllImputationsSelected = () => {
        const displayedParentIds = displayedParentRows
            .map((imputation) => imputationDocumentId(imputation))
            .filter(Boolean);

        setImputationsDeleteSelection(isAllImputationsSelected() ?
            imputationsDeleteSelection.filter(id => !displayedParentIds.includes(id))
            :
            [...new Set([...imputationsDeleteSelection, ...displayedParentIds])]
        );
    }

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (rows) => {
        setPage(1)
        setPageSize(rows)
    }

    const filterOriginValue = useMemo(() =>
        filterOriginSelectOptions.find((o) => o.value === filterOrigin) || filterOriginSelectOptions[0],
    [filterOrigin, filterOriginSelectOptions]);

    const filterSubcategoryValue = useMemo(() =>
        filterSubcategorySelectOptions.find((o) => o.value === filterSubcategory) || filterSubcategorySelectOptions[0],
    [filterSubcategory, filterSubcategorySelectOptions]);

    const approverIdToLabel = useMemo(() => {
        const byId = {};
        approvableUsers.forEach((user) => {
            byId[user.value] = user.label;
        });
        return byId;
    }, [approvableUsers]);

    const groupedImputations = useMemo(() => {
        const byId = {};
        const childrenByParent = {};

        imputations.forEach((row) => {
            const id = imputationDocumentId(row);
            if (!id) return;
            byId[id] = row;
        });

        imputations.forEach((row) => {
            const parentId = row.parent_id ? String(row.parent_id) : null;
            if (!parentId) return;
            if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
            childrenByParent[parentId].push(row);
        });

        const groups = [];
        const seenParents = new Set();
        const rootOrOrphanRows = imputations.filter((row) => {
            const parentId = row.parent_id ? String(row.parent_id) : null;
            return !parentId || !byId[parentId];
        });

        rootOrOrphanRows.forEach((parent) => {
            const parentId = imputationDocumentId(parent);
            if (!parentId || seenParents.has(parentId)) return;
            seenParents.add(parentId);
            groups.push({ parent, children: childrenByParent[parentId] || [] });
        });

        return groups;
    }, [imputations]);

    const displayedGroups = useMemo(() => {
        if (filterSubcategory === "all") return groupedImputations;
        if (filterSubcategory === "with") {
            return groupedImputations.filter((group) => group.children.length > 0);
        }
        return groupedImputations.filter((group) => group.children.length === 0);
    }, [groupedImputations, filterSubcategory]);

    const formatApprovers = useCallback((userIds = []) => {
        if (!Array.isArray(userIds) || userIds.length === 0) return "—";
        return userIds.map((id) => approverIdToLabel[id] || id).join(", ");
    }, [approverIdToLabel]);

    const toggleColumn = useCallback((columnKey) => {
        setVisibleColumns((prev) => ({ ...prev, [columnKey]: !prev[columnKey] }));
    }, []);

    const resetVisibleColumns = useCallback(() => {
        setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    }, []);

    //pagination logic
    const indexOfFirstItem = (page - 1) * pageSize;
    const filteredRowCount = displayedGroups.length;
    const paginatedGroups = useMemo(
        () => displayedGroups.slice(indexOfFirstItem, indexOfFirstItem + pageSize),
        [displayedGroups, indexOfFirstItem, pageSize]
    );
    const totalPages = Math.max(1, Math.ceil(filteredRowCount / pageSize));
    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);
    const lastVisibleItemNumber = Math.min(indexOfFirstItem + pageSize, filteredRowCount);
    const indexOfLastItem = lastVisibleItemNumber > 0 ? lastVisibleItemNumber - 1 : 0;
    const emptyRowsCount = Math.max(0, pageSize - paginatedGroups.length);
    const emptyRows = Array(emptyRowsCount).fill({});
    const isLegalEngine = Utils.isEngineType(data, "legal");
    const tableColSpan = 3
        + (visibleColumns.origin ? 1 : 0)
        + (visibleColumns.departments ? 1 : 0)
        + (showRanksColumn ? 1 : 0)
        + (visibleColumns.glNatural ? 1 : 0)
        + ((isLegalEngine && visibleColumns.costType) ? 1 : 0)
        + (visibleColumns.tax ? 1 : 0)
        + (visibleColumns.approvers ? 1 : 0)
        + (visibleColumns.defaultExpenseType ? 1 : 0)
        + (visibleColumns.clientFilter ? 1 : 0)
        + 1;
    const dataColumnCount = 2
        + (visibleColumns.origin ? 1 : 0)
        + (visibleColumns.departments ? 1 : 0)
        + (showRanksColumn ? 1 : 0)
        + (visibleColumns.glNatural ? 1 : 0)
        + ((isLegalEngine && visibleColumns.costType) ? 1 : 0)
        + (visibleColumns.tax ? 1 : 0)
        + (visibleColumns.approvers ? 1 : 0)
        + (visibleColumns.defaultExpenseType ? 1 : 0)
        + (visibleColumns.clientFilter ? 1 : 0);
    const dataColumnStyle = { width: `calc((100% - 150px) / ${Math.max(1, dataColumnCount)})` };

    const displayedParentRows = useMemo(
        () => paginatedGroups.map((group) => group.parent),
        [paginatedGroups]
    );


    const departmentIdToLabel = useMemo(() => {
        const byId = {};
        departmentOptions.forEach((d) => {
            byId[d.value] = d.label;
        });
        return byId;
    }, [departmentOptions]);

    const formatImputationDepartments = useCallback((imputation) => {
        const ids = normalizeDepartmentIdsFromApi(imputation);
        if (ids.length === 0) return "—";
        return ids.map((id) => departmentIdToLabel[id] || id).join(", ");
    }, [departmentIdToLabel]);


    const rankIdToLabel = useMemo(() => {
        const byId = {};
        rankOptions.forEach((r) => {
            byId[r.value] = r.label;
        });
        return byId;
    }, [rankOptions]);

    const formatImputationRanks = useCallback((imputation) => {
        const ids = normalizeRankIdsFromApi(imputation);
        if (ids.length === 0) return "—";
        return ids.map((id) => rankIdToLabel[id] || id).join(", ");
    }, [rankIdToLabel]);

    const formatDefaultExpenseType = useCallback((imputation) => {
        const val = imputation?.default_expense_type;
        if (!val) return "—";
        return data.t.read(`review_expense.expense_type.${val}`) || val;
    }, [data.t]);

    const formatClientFilter = useCallback((imputation) => {
        const val = imputation?.client_filter;
        if (!val) return "—";
        return data.t.read(`review_expense.expense_type.${val}`) || val;
    }, [data.t]);


    return (

        <div className="card pl-4 pr-4 pb-3 rounded-lg ">
            {isCreditCardImputations ? <CCardsConfigurationNavigationBar data={data} page={"imputations"}/> : <ExpenseNavigationBar data={data} page={"imputations"}/>}
            <div>
                {!loading ?
                    <div>
                        <div>
                            <div className='pagination-row '>
                                <FilterInputField
                                    filterValue={filterValue} setFilterValue={setFilterValue} placeholder={""}>
                                </FilterInputField>
                                <PaginationControls
                                    page={page}
                                    totalPages={totalPages}
                                    indexOfFirstItem={indexOfFirstItem}
                                    indexOfLastItem={indexOfLastItem}
                                    handlePageChange={handlePageChange}
                                    totalItems={filteredRowCount}
                                    data={data}
                                    handleRowsPerPageChange={handleRowsPerPageChange}
                                    rowsPerPage={pageSize}
                                ></PaginationControls>
                            </div>
                            <div className="d-flex flex-wrap align-items-center py-2">
                                <div className="mb-2" style={{ minWidth: "220px" }}>
                                    <label htmlFor="imputation-filter-origin" className="sr-only">{data.t.read("imputation_filter_origin")}</label>
                                    <Select
                                        inputId="imputation-filter-origin"
                                        classNamePrefix="select"
                                        value={filterOriginValue}
                                        options={filterOriginSelectOptions}
                                        onChange={(opt) => {
                                            setFilterOrigin(opt?.value ?? "");
                                            setPage(1);
                                        }}
                                        styles={getSelectStyles(false, false)}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        isSearchable={false}
                                    />
                                </div>
                                <div className="mb-2 ml-2" style={{ minWidth: "260px" }}>
                                    <label htmlFor="imputation-filter-subcategory" className="sr-only">{data.t.read("imputation_filter_subcategory")}</label>
                                    <Select
                                        inputId="imputation-filter-subcategory"
                                        classNamePrefix="select"
                                        value={filterSubcategoryValue}
                                        options={filterSubcategorySelectOptions}
                                        onChange={(opt) => {
                                            setFilterSubcategory(opt?.value ?? "all");
                                            setPage(1);
                                        }}
                                        styles={getSelectStyles(false, false)}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        isSearchable={false}
                                    />
                                </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2">
                                <div className="d-flex">
                                    <button
                                        onClick={() => setShowAddDefaultImputationsModal(true)}
                                        type="button"
                                        disabled={!canEdit}
                                        className="btn btn-secondary btn-sm ml-2">
                                        {readLabel("load_xpens_defaults", "Load XPENS defaults")}
                                    </button>
                                </div>
                                <div className="d-flex">
                                    <div className="position-relative" ref={settingsPanelRef}>
                                        <button
                                            type="button"
                                            className="btn btn-light btn-sm ml-2 d-flex align-items-center justify-content-between"
                                            onClick={() => setIsSettingsOpen((prev) => !prev)}
                                            aria-expanded={isSettingsOpen}
                                        >
                                            <span>
                                                <FontAwesomeIcon icon={faCog} className="mr-2" />
                                                {readLabel("settings", "Settings")}
                                            </span>
                                        </button>
                                        {isSettingsOpen ? (
                                            <div
                                                className="border rounded p-2 bg-white shadow-sm"
                                                style={{
                                                    position: "absolute",
                                                    top: "calc(100% + 6px)",
                                                    right: 0,
                                                    zIndex: 1200,
                                                    width: "280px",
                                                }}
                                            >
                                                <div className="small text-muted mb-2">
                                                    {readLabel("table_config_content", "Choose which columns to display in the table.")}
                                                </div>
                                                <div className="custom-control custom-checkbox mb-1">
                                                    <input
                                                        id="imputations-col-departments"
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        checked={visibleColumns.departments}
                                                        onChange={() => toggleColumn("departments")}
                                                    />
                                                    <label className="custom-control-label" htmlFor="imputations-col-departments">
                                                        {readLabel("imputation_departments", "Departments")}
                                                    </label>
                                                </div>
                                                {hasAderantIntegration ? (
                                                    <div className="custom-control custom-checkbox mb-1">
                                                        <input
                                                            id="imputations-col-ranks"
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            checked={visibleColumns.ranks}
                                                            onChange={() => toggleColumn("ranks")}
                                                        />
                                                        <label className="custom-control-label" htmlFor="imputations-col-ranks">
                                                            {readLabel("imputation_ranks", "Ranks")}
                                                        </label>
                                                    </div>
                                                ) : null}
                                                <div className="custom-control custom-checkbox mb-1">
                                                    <input
                                                        id="imputations-col-origin"
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        checked={visibleColumns.origin}
                                                        onChange={() => toggleColumn("origin")}
                                                    />
                                                    <label className="custom-control-label" htmlFor="imputations-col-origin">
                                                        {readLabel("imputation_origin", "Origin")}
                                                    </label>
                                                </div>
                                                <div className="custom-control custom-checkbox mb-1">
                                                    <input
                                                        id="imputations-col-gl-natural"
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        checked={visibleColumns.glNatural}
                                                        onChange={() => toggleColumn("glNatural")}
                                                    />
                                                    <label className="custom-control-label" htmlFor="imputations-col-gl-natural">
                                                        {readLabel("gl_natural", "GL Natural")}
                                                    </label>
                                                </div>
                                                {isLegalEngine ? (
                                                    <div className="custom-control custom-checkbox mb-1">
                                                        <input
                                                            id="imputations-col-cost-type"
                                                            type="checkbox"
                                                            className="custom-control-input"
                                                            checked={visibleColumns.costType}
                                                            onChange={() => toggleColumn("costType")}
                                                        />
                                                        <label className="custom-control-label" htmlFor="imputations-col-cost-type">
                                                            {readLabel("cost_type", "Cost Type")}
                                                        </label>
                                                    </div>
                                                ) : null}
                                                <div className="custom-control custom-checkbox mb-1">
                                                    <input
                                                        id="imputations-col-tax"
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        checked={visibleColumns.tax}
                                                        onChange={() => toggleColumn("tax")}
                                                    />
                                                    <label className="custom-control-label" htmlFor="imputations-col-tax">
                                                        {readLabel("tax", "Tax")}
                                                    </label>
                                                </div>
                                                <div className="custom-control custom-checkbox mb-1">
                                                    <input
                                                        id="imputations-col-approvers"
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        checked={visibleColumns.approvers}
                                                        onChange={() => toggleColumn("approvers")}
                                                    />
                                                    <label className="custom-control-label" htmlFor="imputations-col-approvers">
                                                        {readLabel("imputation_approvers", "Approvers")}
                                                    </label>
                                                </div>
                                                <div className="custom-control custom-checkbox mb-1">
                                                    <input
                                                        id="imputations-col-default-expense-type"
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        checked={visibleColumns.defaultExpenseType}
                                                        onChange={() => toggleColumn("defaultExpenseType")}
                                                    />
                                                    <label className="custom-control-label" htmlFor="imputations-col-default-expense-type">
                                                        {readLabel("imputation_default_expense_type", "Default client type")}
                                                    </label>
                                                </div>
                                                <div className="custom-control custom-checkbox mb-2">
                                                    <input
                                                        id="imputations-col-client-filter"
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        checked={visibleColumns.clientFilter}
                                                        onChange={() => toggleColumn("clientFilter")}
                                                    />
                                                    <label className="custom-control-label" htmlFor="imputations-col-client-filter">
                                                        {readLabel("imputation_client_filter", "Client filter")}
                                                    </label>
                                                </div>
                                                <button type="button" className="btn btn-link btn-sm p-0" onClick={resetVisibleColumns}>
                                                    {readLabel("reset", "Reset")}
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>

                                    <button
                                        onClick={() => {
                                            setImportFile(null);
                                            setShowImportModal(true);
                                        }}
                                        type="button"
                                        disabled={!canEdit}
                                        className="btn btn-secondary btn-sm ml-2">
                                        {readLabel("import", "Import")}
                                    </button>
                                    <button
                                        disabled={!canEdit || imputationsDeleteSelection.length === 0}
                                        onClick={() => setShowDeleteImputationModal(true)}
                                        type="button"
                                        className="btn btn-secondary btn-sm ml-2">
                                        {data.t.read("delete") + " (" + imputationsDeleteSelection.length + ")"}
                                    </button>
                                    <button
                                        onClick={() => { setImputationModalInitial(null); setShowImputationModal(true); }}
                                        type="button"
                                        disabled={!canEdit}
                                        className='btn btn-primary btn-sm ml-2 justify-content-end'>
                                        {data.t.read("add")}
                                    </button>
                                </div>
                            </div>
                            <div className="mb-2 card rounded-lg admin-table">
                                <table className='table table-hover font-small' style={{ tableLayout: "fixed" }}>
                                    <thead>
                                    <tr>
                                        <th key={`checkbox`} style={{ width: "50px" }}>
                                            <Checkbox
                                                disabled={!displayedParentRows.length}
                                                isChecked={isAllImputationsSelected()}
                                                onChange={toggleAllImputationsSelected}/>
                                        </th>
                                        <th style={dataColumnStyle}>{readLabel("category", "Category")}</th>
                                        {visibleColumns.defaultExpenseType ? <th style={dataColumnStyle}>{readLabel("imputation_default_expense_type", "Default client type")}</th> : null}
                                        {showRanksColumn ? <th style={dataColumnStyle}>{readLabel("imputation_ranks", "Ranks")}</th> : null}
                                        <th style={dataColumnStyle}>{readLabel("field_imputation_subcategory", "Subcategory")}</th>
                                        {visibleColumns.clientFilter ? <th style={dataColumnStyle}>{readLabel("imputation_client_filter", "Client filter")}</th> : null}
                                        {visibleColumns.departments ? <th style={dataColumnStyle}>{readLabel("imputation_departments", "Departments")}</th> : null}
                                        {visibleColumns.origin ? <th style={dataColumnStyle}>{readLabel("imputation_origin", "Origin")}</th> : null}
                                        {visibleColumns.glNatural ? <th style={dataColumnStyle}>{readLabel("gl_natural", "GL Natural")}</th> : null}
                                        {(isLegalEngine && visibleColumns.costType) ? (
                                            <th style={dataColumnStyle}>{readLabel("cost_type", "Cost Type")}</th>
                                        ) : null}
                                        {visibleColumns.tax ? <th style={dataColumnStyle}>{readLabel("tax", "Tax")}</th> : null}
                                        {visibleColumns.approvers ? <th style={dataColumnStyle}>{readLabel("imputation_approvers", "Approvers")}</th> : null}
                                        <th key={"actions"} style={{ width: "100px" }}>
                                            {readLabel("table_actions", "Actions")}
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {paginatedGroups.flatMap(({ parent, children }) => {
                                        const parentId = imputationDocumentId(parent);
                                        const hasChildren = children.length > 0;
                                        const codingRows = hasChildren ? children : [parent];

                                        return codingRows.map((codingRow, index) => (
                                            <tr key={`${parentId}-${imputationDocumentId(codingRow)}-${index}`}>
                                                {index === 0 ? (
                                                    <td rowSpan={codingRows.length}>
                                                        <Checkbox
                                                            isChecked={isImputationSelected(parent)}
                                                            onChange={() => isImputationSelected(parent)
                                                                ? setImputationsDeleteSelection(imputationsDeleteSelection.filter(x => x !== parentId))
                                                                : setImputationsDeleteSelection([...imputationsDeleteSelection, parentId])}
                                                        />
                                                    </td>
                                                ) : null}
                                                {index === 0 ? (
                                                    <td rowSpan={codingRows.length} onClick={() => handleImputationRowClick(parent)}>
                                                        {rowDisplayName(parent)}
                                                        {linkStaleWarning(parent)}
                                                    </td>
                                                ) : null}
                                                {visibleColumns.defaultExpenseType ? (
                                                    index === 0 ? (
                                                        <td rowSpan={codingRows.length} onClick={() => handleImputationRowClick(parent)}>
                                                            {formatDefaultExpenseType(parent)}
                                                        </td>
                                                    ) : null
                                                ) : null}
                                                {showRanksColumn ? (
                                                    index === 0 ? (
                                                        <td
                                                            rowSpan={codingRows.length}
                                                            onClick={() => handleImputationRowClick(parent)}
                                                        >
                                                            {formatImputationRanks(parent)}
                                                        </td>
                                                    ) : null
                                                ) : null}
                                                <td onClick={() => handleImputationRowClick(codingRow)}>
                                                    {hasChildren ? rowDisplayName(codingRow) : "—"}
                                                    {hasChildren ? linkStaleWarning(codingRow) : null}
                                                </td>
                                                {visibleColumns.clientFilter ? (
                                                    <td onClick={() => handleImputationRowClick(codingRow)}>
                                                        {formatClientFilter(codingRow)}
                                                    </td>
                                                ) : null}
                                                {visibleColumns.departments ? (
                                                    <td onClick={() => handleImputationRowClick(codingRow)}>
                                                        {formatImputationDepartments(codingRow)}
                                                    </td>
                                                ) : null}
                                                {visibleColumns.origin ? (
                                                    <td onClick={() => handleImputationRowClick(codingRow)}>
                                                        {originLabel(codingRow.origin)}
                                                    </td>
                                                ) : null}
                                                {visibleColumns.glNatural ? (
                                                    <td onClick={() => handleImputationRowClick(codingRow)}>
                                                        {codingRow.gl_natural || "—"}
                                                    </td>
                                                ) : null}
                                                {(isLegalEngine && visibleColumns.costType) ? (
                                                    <td onClick={() => handleImputationRowClick(codingRow)}>
                                                        {codingRow.cost_type || "—"}
                                                    </td>
                                                ) : null}
                                                {visibleColumns.tax ? (
                                                    <td onClick={() => handleImputationRowClick(codingRow)}>
                                                        {codingRow.tax_code || "—"}
                                                    </td>
                                                ) : null}
                                                {visibleColumns.approvers ? (
                                                    <td onClick={() => handleImputationRowClick(codingRow)}>
                                                        {formatApprovers(codingRow.approver_user_ids)}
                                                    </td>
                                                ) : null}
                                                {index === 0 ? (
                                                    <td rowSpan={codingRows.length}>
                                                        {<FontAwesomeIcon
                                                            className="mr-2"
                                                            icon={faPencilAlt}
                                                            type="button"
                                                            onClick={() => handleImputationRowClick(parent)}
                                                        />}
                                                        {<FontAwesomeIcon
                                                            onClick={() => {
                                                                setShowDeleteImputationModal(true);
                                                                setImputationsDeleteSelection([parentId]);
                                                            }}
                                                            icon={faWindowClose}
                                                            type="button"
                                                            style={{color: 'red'}}/>}
                                                    </td>
                                                ) : null}
                                            </tr>
                                        ));
                                    })}
                                    {
                                        emptyRows.map((_, idx) => (
                                            <tr key={`empty-${idx}`}>
                                                <td colSpan={tableColSpan}>&nbsp;</td>
                                            </tr>
                                        ))
                                    }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {errorMessage != null && (
                            <Modal title="Attention" okText={"OK"} okAction={() => setErrorMessage(null)}>
                                {typeof errorMessage === "string" ? (
                                    <p style={{ whiteSpace: "pre-line" }} className="text-danger">{errorMessage}</p>
                                ) : (
                                    errorMessage
                                )}
                            </Modal>
                        )}
                        {successMessage != null && (
                            <Modal title="Attention" okText={"OK"} okAction={() => setSuccessMessage(null)}>
                                {typeof successMessage === "string" ? (
                                    <p style={{ whiteSpace: "pre-line" }}>{successMessage}</p>
                                ) : (
                                    successMessage
                                )}
                            </Modal>
                        )}
                        {imputationImportResult && (
                            <ImportResultModal
                                data={data}
                                result={imputationImportResult}
                                summarySpec={IMPUTATION_IMPORT_SUMMARY_SPEC}
                                onClose={() => setImputationImportResult(null)}
                            />
                        )}
                        {showImputationModal && (
                            <ImputationConfigModal
                                data={data}
                                initial={imputationModalInitial}
                                imputationCatalog="expense"
                                approvableUsers={approvableUsers}
                                departmentOptions={departmentOptions}
                                rankOptions={rankOptions}
                                onSuccess={(msg) => {
                                    setSuccessMessage(msg);
                                    loadImputationDirectory();
                                    refreshImputations();
                                }}
                                onError={(msg) => setErrorMessage(msg)}
                                onClose={() => setShowImputationModal(false)}
                            />
                        )}
                        {canEdit && showDeleteImputationModal && (
                            <Modal
                                title="Delete"
                                okAction={deleteImputations}
                                cancelAction={cancelDeleteImputationModal}
                                okText={data.t.read("delete")}
                                cancelText={data.t.read("cancel")}>
                                Delete

                            </Modal>
                        )}
                        {showAddDefaultImputationsModal && (
                            <Modal
                                title={data.t.read("default_imputations")}
                                okAction={addDefaultImputations}
                                cancelAction={()=>setShowAddDefaultImputationsModal(false)}
                                okText={data.t.read("add")}
                                cancelText={data.t.read("cancel")}>
                                {data.t.read("default_imputation_confirmation")}
                            </Modal>
                        )}
                        {showImportModal && (
                            <Modal
                                width="50vw"
                                title={readLabel("imputation_import_title", "Import imputations")}
                                okText={readLabel("import", "Import")}
                                okAction={processImputationImport}
                                cancelText={data.t.read("cancel")}
                                cancelAction={() => {
                                    setShowImportModal(false);
                                    setImportFile(null);
                                }}>
                                <div>
                                    <div className="card p-2 mb-2">
                                        <p className="mb-2">{readLabel("imputation_import_help", "Upload a CSV or Excel .xlsx file. Use one row per category or subcategory. When subcategory is filled, category must contain its parent category and coding data belongs to the subcategory.")}</p>
                                        <button
                                            type="button"
                                            className="btn btn-link p-0"
                                            onClick={downloadImputationImportTemplateCsv}
                                        >
                                            {readLabel("supplier_table.download_csv_template", "Download CSV template")}
                                        </button>
                                    </div>
                                    <div className="alert alert-warning py-2 mb-2" role="alert">
                                        <div className="d-flex align-items-center mb-1" style={{ fontWeight: 600 }}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                            {readLabel("imputation_import_rules_title", "Important — before you import")}
                                        </div>
                                        <ul className="mb-0 small pl-3">
                                            <li>{readLabel("imputation_import_rule_category_required", "Category is required on every row. When the Subcategory column has a value, that row creates a child category: put the parent category name in Category and the subcategory name in Subcategory. Coding fields on that row apply to the subcategory.")}</li>
                                            <li>{readLabel("imputation_import_rule_master_data", "GL Natural, GL Office, Cost Type, and Tax code should match values configured for your client (tax codes use your client tax profile). Unknown values are omitted from the imported row and reported as warnings — they are not stored as raw text.")}</li>
                                            <li>{readLabel("imputation_import_rule_approvers", "Approvers are matched by first name and last name, and the name must match exactly one person.")}</li>
                                            <li>{readLabel("imputation_import_rule_departments", "Optional column departments: use pipe-separated department names (e.g. Finance | Legal). Applied to the root category only when that parent is created in this import; reused parents ignore this column.")}</li>
                                        </ul>
                                    </div>
                                    <div className="small text-muted mb-2">
                                        {readLabel("imputation_import_columns", "Required: category on every row. For a subcategory row, subcategory is also required (with the parent name in category). All other columns are optional. Order: category, subcategory, departments, gl_natural, gl_office, cost_type, tax_code, approver_1_first_name, approver_1_last_name, approver_2_first_name, approver_2_last_name. Use | to separate multiple values in departments.")}
                                    </div>
                                    <input
                                        className="form-control-file"
                                        type="file"
                                        accept=".csv,.xlsx"
                                        id="import-imputations-file"
                                        aria-label={readLabel("imputation_import_file", "Imputation import file")}
                                        onChange={f => prepareImputationImport(f.target.files)}
                                    />
                                </div>
                            </Modal>
                        )}
                    </div>
                    :
                    <div className="d-flex loading-container w-100">
                        <FontAwesomeIcon className="infinite-rotate" icon={faSpinner}/>
                    </div>
                }
            </div>
        </div>
    )

}


export default inject('data')(observer(ExpensesImputations));
